import type { CustomType } from "@prismicio/editor-fields"
import { keyBy } from "@prismicio/editor-support/Map"
import {
  Badge,
  Box,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  FormInputField,
} from "@prismicio/editor-ui"
import { type ReactNode, useId, useMemo, useState } from "react"
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form"

import { useCustomTypes } from "../../services/customTypes"

export interface CustomTypesFieldProps<FormSchema extends FieldValues> {
  /**
   * The control object provided by `react-hook-form`'s `useForm` or `useFormContext`.
   * This allows the component to interact with the form state.
   * It is provided from the outside to make `name` prop type-safe.
   */
  control: Control<FormSchema>
  /**
   * The unique name of the field within the form schema.
   * This prop defines which field of the form data this component will manage.
   * In this field it is restricted to data fields of type string[].
   */
  name: Path<FormSchema> & KeysByValues<FormSchema, string[]>

  label?: string
  placeholder?: string
  description?: string
  disabled?: boolean
  customTypeItem: (customType: CustomType) => ReactNode
}

/**
 * A component for selecting Custom Types using a ComboBox,
 * hookable to a react-hook-form field.
 * Ideally, it would be more componentized, but for now, it's a single component
 * (until we improve our ComboBox).
 */
export function HookFormCustomTypesField<FormSchema extends FieldValues>(
  props: CustomTypesFieldProps<FormSchema>,
) {
  const {
    control,
    name,
    label,
    placeholder,
    description,
    customTypeItem,
    disabled,
  } = props

  const customTypeFieldId = useId()

  const customTypes = useCustomTypes().data

  const [customTypeSearchTerm, setCustomTypeSearchTerm] = useState("")

  const filteredCustomTypes = useMemo(
    () =>
      customTypes.filter(
        (customType) =>
          customType.status &&
          customType.label
            .toLowerCase()
            .includes(customTypeSearchTerm.toLowerCase()),
      ),
    [customTypes, customTypeSearchTerm],
  )

  const customTypeNameById = useMemo(
    () =>
      keyBy(
        customTypes,
        (customType) => customType.id,
        (customType) => customType.label,
      ),
    [customTypes],
  )

  const { field, fieldState } = useController<Record<string, string[]>>({
    // @ts-expect-error We can be sure that we will control a string[] field
    // because we have already restricted the name prop to point to a string[]
    control,
    name,
  })

  return (
    <>
      <FormInputField
        id={customTypeFieldId}
        label={label}
        description={description}
        error={fieldState.error?.message}
        disabled={disabled}
      >
        <ComboBox variant="detached" onClose={field.onBlur} forceNoFormInput>
          <ComboBoxInput
            id={customTypeFieldId}
            placeholder={placeholder}
            value={customTypeSearchTerm}
            onValueChange={setCustomTypeSearchTerm}
          />
          <ComboBoxContent paddingBlock={4}>
            {filteredCustomTypes.map((customType) => (
              <ComboBoxItem
                key={customType.id}
                value={customType.id}
                onCheckedChange={() => {
                  if (field.value.includes(customType.id)) {
                    field.onChange(
                      field.value.filter(
                        (customTypeId) => customTypeId !== customType.id,
                      ),
                    )
                  } else {
                    field.onChange([...field.value, customType.id])
                  }
                }}
                checked={field.value.includes(customType.id)}
                disabled={disabled}
              >
                {customTypeItem(customType)}
              </ComboBoxItem>
            ))}
          </ComboBoxContent>
        </ComboBox>
      </FormInputField>

      {field.value.length > 0 && (
        <Box alignItems="flex-start" gap={8} flexWrap="wrap">
          {field.value.map((customTypeId) => (
            <Badge
              key={customTypeId}
              title={customTypeNameById.get(customTypeId)}
              size="medium"
              color="purple"
              selected
              onClose={
                disabled
                  ? undefined
                  : () =>
                      field.onChange(
                        field.value.filter((id) => id !== customTypeId),
                      )
              }
            />
          ))}
        </Box>
      )}
    </>
  )
}

/**
 * Returns the keys of an Obj that have a value of type Value
 */
type KeysByValues<Obj, Value> = {
  [Key in keyof Obj]: Obj[Key] extends Value ? Key : never
}[keyof Obj]
